import { useMemo, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Icon,
  Input,
  Metric,
  Switcher,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { useViewport } from '@/lib/viewport';
import { useAuth } from '@/lib/auth';
import { useScope } from '@/lib/scope';
import { useCurrentConfig } from '@/hooks/useConfig';
import { useCustomers } from '@/hooks/useCustomers';
import { configService } from '@/services/configService';
import { auditService } from '@/services/auditService';
import type { ConfigValue, Customer, PkType, ProductGroup } from '@/domain';

// ─── Constants ───────────────────────────────────────────────────────────────

const PRODUCT_GROUPS: ProductGroup[] = ['COSMETIC', 'FMCG', 'ELECTRONICS', 'APPAREL', 'OTHER'];
const PK_TYPES: PkType[] = ['STANDARD', 'BULKY', 'FRAGILE', 'COMBO'];

const PG_LABEL: Record<ProductGroup, string> = {
  COSMETIC: 'Mỹ phẩm',
  FMCG: 'FMCG',
  ELECTRONICS: 'Điện tử',
  APPAREL: 'Thời trang',
  OTHER: 'Khác',
};
const PK_LABEL: Record<PkType, string> = {
  STANDARD: 'Tiêu chuẩn',
  BULKY: 'Cồng kềnh',
  FRAGILE: 'Dễ vỡ',
  COMBO: 'Combo',
};

const DEFAULT_CUSTOMER_ID = 'DEFAULT';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function cellKey(customerId: string, pg: ProductGroup, pk: PkType): string {
  return `${customerId}:${pg}:${pk}`;
}

function buildNormsLookup(
  norms: ConfigValue[],
  warehouseId: string,
): Map<string, ConfigValue> {
  // map: "{customer_id}:{pg}:{pk}" → ConfigValue
  const m = new Map<string, ConfigValue>();
  for (const n of norms) {
    const parts = n.key.split(':');
    if (parts[1] !== warehouseId) continue;
    const k = `${parts[2]}:${parts[3]}:${parts[4]}`;
    m.set(k, n);
  }
  return m;
}

interface CoverageInfo {
  count: number;
  total: number; // = product_groups.length for current pk
}

// ─── Component ────────────────────────────────────────────────────────────────

export function BulkNormsConfigPage() {
  const { isMobile } = useViewport();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { warehouses, currentWarehouse, setWarehouseId } = useScope();

  const norms = useCurrentConfig('UPH_NORM');
  const customers = useCustomers();

  const [whFilter, setWhFilter] = useState<string>(currentWarehouse?.id ?? '');
  const [pkFilter, setPkFilter] = useState<PkType>('STANDARD');
  const [custSearch, setCustSearch] = useState('');
  const [pending, setPending] = useState<Map<string, number>>(new Map());
  const [saving, setSaving] = useState(false);

  const targetWh = warehouses.find((w) => w.id === (whFilter || currentWarehouse?.id)) ?? null;
  const activeCustomers = useMemo(
    () => customers.filter((c) => c.is_active),
    [customers],
  );

  // All norms for this warehouse, keyed by `${cust}:${pg}:${pk}`
  const lookup = useMemo(
    () => (targetWh ? buildNormsLookup(norms, targetWh.id) : new Map<string, ConfigValue>()),
    [norms, targetWh],
  );

  // Filtered customer list (search)
  const filteredCustomers = useMemo(() => {
    const needle = custSearch.toLowerCase();
    return needle
      ? activeCustomers.filter(
          (c) => c.code.toLowerCase().includes(needle) || c.name.toLowerCase().includes(needle),
        )
      : activeCustomers;
  }, [activeCustomers, custSearch]);

  // Coverage per customer (for the current pk_type)
  function getCoverage(customerId: string): CoverageInfo {
    let count = 0;
    for (const pg of PRODUCT_GROUPS) {
      if (lookup.has(cellKey(customerId, pg, pkFilter))) count++;
    }
    return { count, total: PRODUCT_GROUPS.length };
  }

  // Effective value for a cell: pending change → saved value → null
  function getEffectiveValue(customerId: string, pg: ProductGroup): number | null {
    const pkey = `${customerId}:${pg}`;
    if (pending.has(pkey)) return pending.get(pkey) ?? null;
    const saved = lookup.get(cellKey(customerId, pg, pkFilter));
    return saved?.value ?? null;
  }

  function isPending(customerId: string, pg: ProductGroup): boolean {
    return pending.has(`${customerId}:${pg}`);
  }

  function setCellValue(customerId: string, pg: ProductGroup, raw: string) {
    const num = Number(raw);
    const pkey = `${customerId}:${pg}`;
    const next = new Map(pending);
    if (raw === '' || Number.isNaN(num) || num <= 0) {
      // empty → clear pending (back to saved value)
      next.delete(pkey);
    } else {
      // only mark pending if different from saved
      const saved = lookup.get(cellKey(customerId, pg, pkFilter))?.value ?? null;
      if (saved === Math.round(num)) {
        next.delete(pkey);
      } else {
        next.set(pkey, Math.round(num));
      }
    }
    setPending(next);
  }

  function discardChanges() {
    setPending(new Map());
  }

  async function saveAll() {
    if (!targetWh || !user || pending.size === 0) return;
    setSaving(true);
    try {
      const entries = [...pending.entries()];
      for (const [pkey, value] of entries) {
        const [customerId, pg] = pkey.split(':') as [string, ProductGroup];
        await configService.setValue({
          key: {
            kind: 'UPH_NORM',
            warehouse_id: targetWh.id,
            customer_id: customerId,
            product_group: pg,
            pk_type: pkFilter,
          },
          value,
          note: 'bulk-edit',
          actor: user.email,
        });
      }
      await auditService.log({
        actor: user.email,
        action: 'CONFIG_CHANGE',
        entity_type: 'config_value',
        entity_id: `uph:${targetWh.id}:bulk`,
        summary: `Bulk edit ${entries.length} UPH norms · ${targetWh.code} · ${pkFilter}`,
        payload: { count: entries.length, warehouse: targetWh.code, pk_type: pkFilter, mode: 'bulk' },
      });
      setPending(new Map());
    } finally {
      setSaving(false);
    }
  }

  // ── Stats ────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    let configuredCustomers = 0;
    let totalCells = 0;
    for (const c of activeCustomers) {
      const cov = getCoverage(c.id);
      if (cov.count > 0) configuredCustomers++;
      totalCells += cov.count;
    }
    return {
      total: activeCustomers.length,
      configured: configuredCustomers,
      totalCells,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCustomers, lookup, pkFilter]);

  // ── Render row ───────────────────────────────────────────────────────────

  function renderCustomerRow(customer: Customer | null, isDefaultRow: boolean) {
    const customerId = isDefaultRow ? DEFAULT_CUSTOMER_ID : customer!.id;
    const cov = !isDefaultRow ? getCoverage(customerId) : null;

    return (
      <tr
        key={customerId}
        style={{
          background: isDefaultRow ? 'rgba(11,67,234,0.04)' : '#fff',
          borderBottom: '1px solid var(--bx-border-muted)',
        }}
      >
        {/* Customer code + name */}
        <td
          style={{
            padding: '8px 10px',
            position: 'sticky',
            left: 0,
            background: isDefaultRow ? '#eef2ff' : '#fff',
            borderRight: '1px solid var(--bx-border-muted)',
            zIndex: 1,
            minWidth: 200,
          }}
        >
          {isDefaultRow ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="layers" size={13} style={{ color: 'var(--bx-primary)' }} />
              <div>
                <div style={{ fontWeight: 700, color: 'var(--bx-primary)', fontSize: 13 }}>
                  Mặc định
                </div>
                <div style={{ fontSize: 11, color: 'var(--bx-text-muted)' }}>
                  Áp dụng khi khách hàng chưa cấu hình
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--bx-text-dark)' }}>
                {customer!.code}
              </div>
              <div style={{ fontSize: 11, color: 'var(--bx-text-muted)' }}>
                {customer!.name}
              </div>
            </div>
          )}
        </td>

        {/* PG cells */}
        {PRODUCT_GROUPS.map((pg) => {
          const value = getEffectiveValue(customerId, pg);
          const isDirty = isPending(customerId, pg);
          const savedDefault =
            !isDefaultRow ? lookup.get(cellKey(DEFAULT_CUSTOMER_ID, pg, pkFilter))?.value ?? null : null;
          return (
            <td key={pg} style={{ padding: '4px 6px', minWidth: 110 }}>
              <BulkCell
                value={value}
                fallback={savedDefault}
                dirty={isDirty}
                onChange={(e) => setCellValue(customerId, pg, e.target.value)}
              />
            </td>
          );
        })}

        {/* Coverage badge */}
        <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
          {isDefaultRow ? (
            <Button
              variant="ghost"
              size="sm"
              icon="external-link"
              onClick={() => navigate('/admin/config/dinhmuc/mac-dinh')}
            >
              Chi tiết
            </Button>
          ) : cov ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
              <Badge
                kind={cov.count === cov.total ? 'ACTIVE' : cov.count > 0 ? 'WARNING' : 'INFO'}
                size="sm"
              >
                {cov.count}/{cov.total}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/config/dinhmuc-khachhang')}
                style={{ fontSize: 11, padding: '2px 6px' }}
              >
                Xem
              </Button>
            </div>
          ) : null}
        </td>
      </tr>
    );
  }

  const pendingCount = pending.size;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Cấu hình', 'Tham số vận hành', 'Định mức', 'Bảng tổng hợp']}
        title="Bảng định mức UPH"
        subtitle="Edit nhanh nhiều khách hàng cùng lúc. Lưu thay đổi tạo version mới + audit."
        actions={
          <>
            <Button variant="outline" icon="layers" onClick={() => navigate('/admin/config/dinhmuc/mac-dinh')}>
              Định mức mặc định
            </Button>
            <Switcher
              icon="warehouse"
              label="Kho"
              value={targetWh?.id ?? ''}
              options={warehouses.map((w) => ({ value: w.id, label: w.code, sublabel: w.name }))}
              onChange={(v) => { setWhFilter(v); setWarehouseId(v); }}
              width={180}
            />
            <Switcher
              icon="package"
              label="Loại pack"
              value={pkFilter}
              options={PK_TYPES.map((p) => ({ value: p, label: PK_LABEL[p], sublabel: p }))}
              onChange={(v) => {
                if (pending.size > 0 && !confirm('Có thay đổi chưa lưu. Đổi loại pack sẽ mất các thay đổi đó?')) return;
                setPkFilter(v as PkType);
                setPending(new Map());
              }}
              width={180}
            />
            <Button variant="outline" icon="user" onClick={() => navigate('/admin/config/dinhmuc-khachhang')}>
              Xem 1 KH
            </Button>
          </>
        }
      />

      <div style={{ flex: 1, padding: isMobile ? 14 : 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Metrics */}
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <Metric label="Khách hàng" value={stats.total} sublabel="đang hoạt động" />
          <Metric label="Đã cấu hình" value={stats.configured} sublabel={`/ ${stats.total}`} deltaTone="up" />
          <Metric
            label="Tổng cells đã set"
            value={stats.totalCells}
            sublabel={`pk_type = ${pkFilter}`}
          />
          <Metric
            label="Đang sửa"
            value={pendingCount}
            sublabel={pendingCount > 0 ? 'chưa lưu' : 'không có thay đổi'}
            deltaTone={pendingCount > 0 ? 'down' : undefined}
          />
        </div>

        {/* Action bar */}
        <Card padding={0}>
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid var(--bx-border-muted)',
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ width: 280, minWidth: 200 }}>
              <Input
                icon="search"
                placeholder="Tìm khách hàng…"
                value={custSearch}
                onChange={(e) => setCustSearch(e.target.value)}
                size="sm"
              />
            </div>
            <div style={{ flex: 1 }} />
            {pendingCount > 0 && (
              <>
                <span style={{ fontSize: 12.5, color: '#d97706', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Icon name="warning" size={13} />
                  {pendingCount} thay đổi chưa lưu
                </span>
                <Button variant="outline" size="sm" onClick={discardChanges} disabled={saving}>
                  Hủy
                </Button>
                <Button variant="primary" size="sm" icon="check" onClick={saveAll} disabled={saving}>
                  {saving ? 'Đang lưu…' : `Lưu ${pendingCount} thay đổi`}
                </Button>
              </>
            )}
          </div>

          {/* Spreadsheet table */}
          {!targetWh ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--bx-text-muted)' }}>
              Chọn kho ở thanh trên để xem bảng
            </div>
          ) : (
            <div style={{ overflowX: 'auto', maxHeight: '70vh' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead
                  style={{
                    position: 'sticky',
                    top: 0,
                    background: 'var(--bx-surface-2)',
                    zIndex: 2,
                  }}
                >
                  <tr>
                    <th
                      style={{
                        ...headerCellStyle,
                        textAlign: 'left',
                        position: 'sticky',
                        left: 0,
                        background: 'var(--bx-surface-2)',
                        borderRight: '1px solid var(--bx-border-muted)',
                        zIndex: 3,
                        minWidth: 200,
                      }}
                    >
                      Khách hàng
                    </th>
                    {PRODUCT_GROUPS.map((pg) => (
                      <th key={pg} style={headerCellStyle}>
                        <div style={{ fontWeight: 600 }}>{PG_LABEL[pg]}</div>
                        <div style={{ fontSize: 10, color: 'var(--bx-text-muted)', fontFamily: 'var(--bx-font-mono)' }}>
                          {pg}
                        </div>
                      </th>
                    ))}
                    <th style={{ ...headerCellStyle, textAlign: 'right' }}>Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {renderCustomerRow(null, true)}
                  {filteredCustomers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={PRODUCT_GROUPS.length + 2}
                        style={{
                          padding: 24,
                          textAlign: 'center',
                          color: 'var(--bx-text-muted)',
                          fontSize: 12.5,
                        }}
                      >
                        Không tìm thấy khách hàng phù hợp
                      </td>
                    </tr>
                  ) : (
                    filteredCustomers.map((c) => renderCustomerRow(c, false))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Help */}
        <Card style={{ padding: '10px 14px' }}>
          <div style={{ display: 'flex', gap: 8, fontSize: 12.5, color: 'var(--bx-text-muted)', lineHeight: 1.55, alignItems: 'flex-start' }}>
            <Icon name="info" size={13} style={{ color: 'var(--bx-primary)', marginTop: 2, flexShrink: 0 }} />
            <span>
              Ô có chấm cam = đang sửa (chưa lưu). Số mờ trong ngoặc = giá trị mặc định khi ô trống.
              Đổi loại pack (STANDARD/BULKY/…) ở thanh trên để chuyển ma trận. Nhấn Tab/Enter để di chuyển nhanh.
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const headerCellStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'right',
  fontSize: 11.5,
  fontWeight: 600,
  color: 'var(--bx-text-body)',
  borderBottom: '1px solid var(--bx-border-muted)',
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

interface BulkCellProps {
  value: number | null;
  fallback: number | null;
  dirty: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

function BulkCell({ value, fallback, dirty, onChange }: BulkCellProps) {
  const display = value != null ? String(value) : '';
  const placeholder = fallback != null ? `(${fallback})` : '—';
  return (
    <div style={{ position: 'relative' }}>
      <input
        type="number"
        min={1}
        value={display}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          width: '100%',
          height: 32,
          padding: '0 8px',
          border: '1px solid ' + (dirty ? '#d97706' : 'var(--bx-border)'),
          borderRadius: 8,
          background: dirty ? '#fff7ed' : '#fff',
          fontFamily: 'var(--bx-font-mono)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 13,
          fontWeight: value != null ? 600 : 400,
          textAlign: 'right',
          color: value != null ? 'var(--bx-text-dark)' : 'var(--bx-text-muted)',
          outline: 'none',
        }}
      />
      {dirty && (
        <span
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: '#d97706',
          }}
        />
      )}
    </div>
  );
}
